import { CARTOPOUSSE_BASE_URL } from '../utils/ApiUrls';

class TaskService {

  async createTask(taskData, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(taskData),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.detail || 'Task creation failed', errorData: data };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async getMyTasks(accessToken, pagination_data) {
    // Build query parameters
    const queryParams = new URLSearchParams ();
    try {

      if (pagination_data.page) {
        queryParams.append('page', pagination_data.page);
      }
      if (pagination_data.page_size) {
        queryParams.append('page_size', pagination_data.page_size);
      }
      const queryString = queryParams.toString();
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/my-tasks/${queryString ? `?${queryString}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.detail || 'Failed to fetch tasks' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async getOrganizationTasks(accessToken, pagination_data) {
    const queryParams = new URLSearchParams ();
    try {

      if (pagination_data.page) {
        queryParams.append('page', pagination_data.page);
      }
      if (pagination_data.page_size) {
        queryParams.append('page_size', pagination_data.page_size);
      }
      const queryString = queryParams.toString();
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/organization/${queryString ? `?${queryString}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.detail || 'Failed to fetch organization tasks' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async getTaskDetails(taskId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.detail || 'Failed to fetch task details' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  

  async updateTaskStatus(taskId, status, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/update-status/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.detail || 'Failed to update task status' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  // Convenience methods for task workflow
  async acceptTask(taskId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/accept/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.error || 'Failed to accept task' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async rejectTask(taskId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/reject/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.error || 'Failed to reject task' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async startTask(taskId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/start/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.error || 'Failed to start task' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async completeTask(taskId, accessToken, comment) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/complete/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(
          comment ? { comment } : {}
        ),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.error || 'Failed to complete task' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  // Helper method to get available actions based on task status
  getAvailableActions(taskStatus) {
    const actions = {
      'pending': ['accept', 'reject'],
      'accepted': ['start'],
      'rejected': [],
      'in_progress': ['complete'],
      'completed': []
    };
    return actions[taskStatus] || [];
  }

  // Helper method to get status display information
  getStatusInfo(status) {
    const statusInfo = {
      'pending': { label: 'En attente', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      'accepted': { label: 'Acceptée', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      'rejected': { label: 'Rejetée', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' },
      'in_progress': { label: 'En cours', color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-800' },
      'completed': { label: 'Terminée', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' }
    };
    return statusInfo[status] || { label: status, color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
  }

  // ===================== Attachment Methods =====================

  async uploadTaskAttachment(taskId, formData, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/attachments/upload/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.error || 'Failed to upload attachment' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async getTaskAttachments(taskId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/tasks/${taskId}/attachments/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.error || 'Failed to fetch attachments' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async downloadTaskAttachment(attachmentId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/attachments/${attachmentId}/download/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, status: response.status, error: errorData.error || 'Failed to download attachment' };
      }
      
      // Return the response for blob handling
      return { success: true, status: response.status, response };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  async deleteTaskAttachment(attachmentId, accessToken) {
    try {
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/attachments/${attachmentId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, status: response.status, error: data.error || 'Failed to delete attachment' };
      }
      return { success: true, status: response.status, data };
    } catch (error) {
      return { success: false, status: 0, error: error.message || 'Network error' };
    }
  }

  // Helper method to format file size
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Helper method to validate file type
  isValidFileType(fileName) {
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif'];
    const fileExtension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return allowedExtensions.includes(fileExtension);
  }

  // Helper method to validate file size (5MB limit)
  isValidFileSize(fileSize) {
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    return fileSize <= maxSize;
  }
}

const taskService = new TaskService();
export default taskService;
